import { h } from 'vue';
import { createColumnHelper } from '@tanstack/vue-table';
import { OFFER_STATUS } from '@/utils/enum/offerStatus';
import Request from '@/services/request';
import ProductInfoCell from '@/components/DataTable/ProductInfoCell';
import store from '@/store';
import { sendViewProductDealsEvent } from '@/utils/ga4/helper';
import {
  PRODUCT_DEALS_APPLY,
  PRODUCT_DEALS,
  PRODUCT_DEAL_GOTO_PRODUCT_DETAIL,
  PRODUCT_DEAL_REJECT_OFFER,
} from '@/utils/enum/endpoints';
import SellerPriceOffer from '../partials/SellerPriceOffer';
import SalesOffer from '../partials/SalesOffer';

// Single request object for all requests in a scope
const request = new Request();
const columnHelper = createColumnHelper();

const handleProductDetail = async (pid) => {
  try {
    const response = await request.get(
      PRODUCT_DEAL_GOTO_PRODUCT_DETAIL.replace('{pid}', pid),
    );
    const url = response.data.productUrl;
    url && window.open(url, '_blank');
  } catch (error) {
    console.error('Error fetching product details:', error);
  }
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  let d = null;
  try {
    d = new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch (e) {
    console.error('format error', e);
  }

  return d;
};

const generateProductInfoForDeal = (row) => {
  return {
    dealType: row.original.dealType,
    imageUrl: row.original.dealDto.productImage,
    title: row.original.dealDto.productName,
    productCode: row.original.dealDto.productId,
    stockCode: row.original.dealDto.sellerStockCode,
    stock: row.original.dealDto.stockCount,
    acceptedDate: formatDateTime(row.original.dealDto?.acceptedDate),
    revokedDate: formatDateTime(row.original.dealDto?.revokedDate),
    displayPrice: row.original.dealDto.displayPrice,
    commissionRate: row.original.dealDto.commissionRate,
    startDate: formatDateTime(row.original.dealDto.startDate),
    finishDate: formatDateTime(row.original.dealDto.finishDate),
    acceptedOffer: row.original.acceptedDealDto
      ? {
          price: row.original.acceptedDealDto.displayPrice,
          commission: row.original.acceptedDealDto.commissionRate,
          startDate: formatDateTime(row.original.acceptedDealDto.acceptedDate),
          finishDate: formatDateTime(row.original.acceptedDealDto.finishDate),
        }
      : null,
    rejectedOffer: getRejectedOffer(row.original.dealDto),
  };
};

const removeBlankAttributes = (obj) => {
  const result = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      result[key] = obj[key];
    }
  });
  return result;
};

export const columns = [
  columnHelper.accessor('productName', {
    header: () => 'Ürün Bilgisi',
    cell: ({ row }) => {
      const pid = row.original.dealDto.productId;
      return h(ProductInfoCell, {
        productInfo: generateProductInfoForDeal(row),
        type: 'deal',
        class: 'hunt',
        isClickable: true,
        onClick: () => handleProductDetail(pid),
      });
    },
    size: 366,
  }),
  columnHelper.accessor('advantageousPrice', {
    header: 'Avantajlı Teklif',
    cell: ({ row }) => {
      const { dealDto, dealType } = row.original;

      if (dealDto.advantageousOffer) {
        return h(SalesOffer, {
          dealType,
          dealStatus: dealDto.advantageousOffer.offerStatus,
          upperPrice: dealDto.advantageousOffer.upperPrice,
          lowerPrice: dealDto.advantageousOffer.lowerPrice,
          offerPrice: dealDto.advantageousOffer.offerPrice,
          commission: dealDto.advantageousOffer.commission,
          offerStatus: dealDto.advantageousOffer.offerStatus,
          startDate: dealDto.advantageousOffer.offerAcceptedDate
            ? new Date(dealDto.advantageousOffer.offerAcceptedDate)
            : null,
          endDate: dealDto.advantageousOffer.offerRejectedDate
            ? new Date(dealDto.advantageousOffer.offerRejectedDate)
            : null,
          plannedStartDate: new Date(dealDto.startDate),
          plannedEndDate: new Date(dealDto.finishDate),
          pid: dealDto.productId,
          dealId: dealDto.dealId,
          groupId: dealDto.groupId,
          displayPrice: dealDto.displayPrice,
          categoryId: dealDto.categoryId,
          hasVariants: dealDto.hasVariants,
        });
      } else {
        return h(SellerPriceOffer);
      }
    },
    size: 254,
  }),
  columnHelper.accessor('superPrice', {
    header: 'Süper Teklif',
    cell: ({ row }) => {
      const { dealDto, dealType } = row.original;

      if (dealDto.superOffer) {
        return h(SalesOffer, {
          dealType,
          dealStatus: dealDto.superOffer.offerStatus,
          upperPrice: dealDto.superOffer.upperPrice,
          lowerPrice: dealDto.superOffer.lowerPrice,
          offerPrice: dealDto.superOffer.offerPrice,
          commission: dealDto.superOffer.commission,
          offerStatus: dealDto.superOffer.offerStatus,
          isRefused: dealDto.superOffer.offerStatus === OFFER_STATUS.REFUSED,
          startDate: dealDto.superOffer.offerAcceptedDate
            ? new Date(dealDto.superOffer.offerAcceptedDate)
            : null,
          endDate: dealDto.superOffer.offerRejectedDate
            ? new Date(dealDto.superOffer.offerRejectedDate)
            : null,
          plannedStartDate: new Date(dealDto.startDate),
          plannedEndDate: new Date(dealDto.finishDate),
          pid: dealDto.productId,
          dealId: dealDto.dealId,
          groupId: dealDto.groupId,
          displayPrice: dealDto.displayPrice,
          categoryId: dealDto.categoryId,
          hasVariants: dealDto.hasVariants,
        });
      } else {
        return h(SellerPriceOffer);
      }
    },
    size: 254,
  }),
  columnHelper.accessor('shockingPrice', {
    header: 'Kaçmaz Teklif',
    cell: ({ row }) => {
      const { dealDto, dealType } = row.original;
      if (dealDto.shockingOffer) {
        return h(SalesOffer, {
          dealType,
          dealStatus: dealDto.shockingOffer.offerStatus,
          upperPrice: dealDto.shockingOffer.upperPrice,
          lowerPrice: 0,
          offerPrice: dealDto.shockingOffer.offerPrice,
          commission: dealDto.shockingOffer.commission,
          offerStatus: dealDto.shockingOffer.offerStatus,
          isRefused: dealDto.shockingOffer.offerStatus === OFFER_STATUS.REFUSED,
          startDate: dealDto.shockingOffer.offerAcceptedDate
            ? new Date(dealDto.shockingOffer.offerAcceptedDate)
            : null,
          endDate: dealDto.shockingOffer.offerRejectedDate
            ? new Date(dealDto.shockingOffer.offerRejectedDate)
            : null,
          plannedStartDate: new Date(dealDto.startDate),
          plannedEndDate: new Date(dealDto.finishDate),
          pid: dealDto.productId,
          dealId: dealDto.dealId,
          groupId: dealDto.groupId,
          displayPrice: dealDto.displayPrice,
          categoryId: dealDto.categoryId,
          hasVariants: dealDto.hasVariants,
        });
      } else {
        return h(SellerPriceOffer);
      }
    },
    size: 254,
  }),
  columnHelper.accessor('sellerPriceOffer', {
    header: 'Mevcut Fiyat & Komisyon',
    cell: ({ row }) => {
      const { dealDto, dealType } = row.original;
      return h(SellerPriceOffer, {
        dealType,
        commission: dealDto.commissionRate,
        price: dealDto.displayPrice,
        dealStatus: row.original.dealStatus,
        startDate: new Date(dealDto.startDate),
        dealId: dealDto.dealId,
        groupId: dealDto.groupId,
        categoryId: dealDto.categoryId,
        productId: dealDto.productId,
        commissionRates: commissionRates(dealDto),
        dealStatuses: dealStatuses(dealDto),
        hasVariants: dealDto.hasVariants,
        index: row.index,
      });
    },
    size: 234,
  }),
];

export const getTableDataAsync = async (payload) => {
  const response = {
    data: null,
    error: null,
    pageCount: 0,
    periodList: [],
  };

  const sellerId = store.getters['auth/getSellerId'];
  const requestParams = removeBlankAttributes(payload);

  try {
    const result = await request.get(PRODUCT_DEALS, requestParams, {
      baseURL: '/sellerofficeapi/rest',
      headers: {
        Accept: 'application/json',
      },
    });

    response.pageCount = result.data.pagingModel.pageCount;
    response.data = result.data.sellerProductDealPairDtos.map(
      (productDealDto) => {
        const offerStatusList = [
          productDealDto.dealDto.advantageousOffer?.offerStatus,
          productDealDto.dealDto.superOffer?.offerStatus,
          productDealDto.dealDto.shockingOffer?.offerStatus,
        ];

        return {
          ...productDealDto,
          dealStatus: getDealStatus(offerStatusList),
          dealType: productDealDto.dealType,
        };
      },
    );

    sendViewProductDealsEvent({
      deal_status: response.pageCount > 0 ? 'available' : 'unavailable',
      user_id: sellerId,
    });
  } catch (err) {
    response.error = err;
  }

  return { ...response };
};

const getDealStatus = (offerStatusList) => {
  const statusPriority = [
    OFFER_STATUS.REFUSED,
    OFFER_STATUS.ACCEPTED,
    OFFER_STATUS.WAITING_FOR_APPLYING,
  ];

  return (
    statusPriority.find((status) => offerStatusList.includes(status)) ||
    OFFER_STATUS.WAITING_FOR_ACCEPTANCE
  );
};

export const modifyDeal = async ({ dealId, groupId, price, productId }) => {
  return await request.post(
    PRODUCT_DEALS_APPLY.replace('{id}', dealId),
    {
      groupId: groupId,
      price: price,
      productId: productId,
    },
    {
      baseURL: '/sellerofficeapi/rest',
      headers: {
        Accept: 'application/json',
      },
    },
  );
};

export const applyDeal = async ({ dealId, groupId, price, productId }) => {
  return await request.post(
    PRODUCT_DEALS_APPLY.replace('{id}', dealId),
    {
      groupId: groupId,
      price: price,
      productId: productId,
    },
    {
      baseURL: '/sellerofficeapi/rest',
      headers: {
        Accept: 'application/json',
      },
    },
  );
};

export const commissionRates = (data) => {
  return {
    ...(data.advantageousOffer
      ? {
          advantageousOffer: {
            upperPrice: data.advantageousOffer.upperPrice,
            lowerPrice: data.advantageousOffer.lowerPrice,
            commission: data.advantageousOffer.commission,
          },
        }
      : []),
    ...(data.superOffer
      ? {
          superOffer: {
            upperPrice: data.superOffer.upperPrice,
            lowerPrice: data.superOffer.lowerPrice,
            commission: data.superOffer.commission,
          },
        }
      : []),
    ...(data.shockingOffer
      ? {
          shockingOffer: {
            upperPrice: data.shockingOffer.upperPrice,
            lowerPrice: data.shockingOffer.lowerPrice,
            commission: data.shockingOffer.commission,
          },
        }
      : []),
  };
};

export const dealStatuses = (data) => {
  return {
    advantageousOffer: data.advantageousOffer?.offerStatus,
    superOffer: data.superOffer?.offerStatus,
    shockingOffer: data.shockingOffer?.offerStatus,
  };
};

export const getRejectedOffer = (dealDto) => {
  const offers = [
    dealDto?.advantageousOffer,
    dealDto?.superOffer,
    dealDto?.shockingOffer,
  ];

  const rejectedOfferData = offers.find(
    (offer) => offer?.offerStatus === 'DELETED_BY_BO_OPERATOR',
  );
  if (!rejectedOfferData) return null;

  return {
    offerPrice: rejectedOfferData.offerPrice,
    commission: rejectedOfferData.commission,
    offerAcceptedDate: rejectedOfferData.offerAcceptedDate
      ? formatDateTime(rejectedOfferData.offerAcceptedDate)
      : null,
    offerRejectedDate: rejectedOfferData.offerRejectedDate
      ? formatDateTime(rejectedOfferData.offerRejectedDate)
      : null,
  };
};

export const rejectOffer = async ({ dealId, groupId }) => {
  return await request.post(
    PRODUCT_DEAL_REJECT_OFFER.replace('{id}', dealId),
    {
      groupId: groupId,
    },
    {
      baseURL: '/sellerofficeapi/rest',
      headers: {
        Accept: 'application/json',
      },
    },
  );
};
